// File: TestReport.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import {
  RefreshCw,
  BarChart2,
  Download,
  Play,
  Pause,
  Search,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend
} from 'recharts';
import api from "../../../utils/api"; 
import { BASE_URL, objectivequizReportApi } from "../../../api/api";
import './TestReport.css';

// Utility: create CSV from table rows
const toCSV = (rows) => {
  const header = ['id','quiz_id','student_id','question_id','selected_option','correct_option','obtained_marks','remarks'];
  const csv = [header.join(',')]
    .concat(rows.map(r => header.map(h => JSON.stringify(r[h] ?? '')).join(',')))
    .join('\n');
  return csv;
};

export default function ObjectiveTestReport () {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [query, setQuery] = useState('');
  const [live, setLive] = useState(true);
  const [chartData, setChartData] = useState([]);
  const liveRef = useRef(live);

  useEffect(()=>{ liveRef.current = live; }, [live]);

  //  Fetch report data from API
  async function fetchReport() {
    try {
      const res = await api.get(`${objectivequizReportApi}`);

      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];

      setRows(data);
      setChartData(generateChartData(data));
      toast.success("Report loaded successfully");
    } catch (err) {
      console.error("Error fetching report:", err);
      toast.error("Failed to load report data");
    }
  }

  useEffect(() => {
    fetchReport();
  }, []);

  // Filter rows by quick search
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFilteredRows(rows);
      return;
    }
    setFilteredRows(
      rows.filter(r =>
        Object.values(r).some(v => String(v).toLowerCase().includes(q))
      )
    );
  }, [rows, query]);

  function handleRefresh(){
    fetchReport(); //  Now refresh fetches new data
  }

  function handleExport(){
    if (!rows.length) return toast.error("No data to export");
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-report-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.info('CSV download started');
  }

  function toggleLive(){
    setLive(l => !l);
    toast(live ? 'Live updates paused' : 'Live updates resumed');
  }

  const marksSummary = useMemo(() => {
    const total = rows.length;
    const obtained = rows.reduce((s,r)=> s + (Number(r.obtained_marks) || 0), 0);
    return { total, obtained, percent: total? Math.round(obtained/total*100):0 };
  }, [rows]);

  return (
    <div className="test-report-root">

      <header className="test-report-header" role="banner">
        <div className="test-report-header-left">
          <div className="test-report-brand">Test Report</div>
          <div className="test-report-sub">Performance & live insights</div>
        </div>

        <div className="test-report-header-right">
          <div className="test-report-stats">
            <div className="test-report-stat">
              <div className="test-report-stat-value">{marksSummary.obtained}/{marksSummary.total}</div>
              <div className="test-report-stat-label">Correct answers</div>
            </div>
            <div className="test-report-stat">
              <div className="test-report-stat-value">{marksSummary.percent}%</div>
              <div className="test-report-stat-label">Average score</div>
            </div>
          </div>

          <div className="test-report-actions">
            <button className="test-report-btn" onClick={handleRefresh} title="Refresh"><RefreshCw size={16} /></button>
            <button className="test-report-btn" onClick={handleExport} title="Export CSV"><Download size={16} /></button>
            <button className="test-report-btn test-report-live" onClick={toggleLive} title="Toggle live updates">{live ? <Pause size={14}/> : <Play size={14}/>}</button>
          </div>
        </div>
      </header>

      <main className="test-report-main">
        <section className="test-report-visuals">
          <div className="test-report-leftpane">
            <div className="test-report-control">
              <div className="test-report-search">
                <input
                  className="test-report-search-input"
                  value={query}
                  onChange={(e)=>setQuery(e.target.value)}
                  placeholder="Quick search — id, quiz id, student..."
                />
                <button className="test-report-search-btn" aria-label="search"><Search size={14} /></button>
              </div>

              <div className="test-report-mini-card">
                <div className="test-report-mini-left">
                  <BarChart2 size={26} />
                </div>
                <div className="test-report-mini-right">
                  <div className="test-report-mini-title">Live Trend</div>
                  <div className="test-report-mini-value">{marksSummary.percent}% avg</div>
                </div>
              </div>

              <div className="test-report-legend">
                <div className="test-report-legend-item"><span className="dot good"/> Correct</div>
                <div className="test-report-legend-item"><span className="dot bad"/> Wrong</div>
                <div className="test-report-legend-item"><span className="dot neutral"/> No remark</div>
              </div>
            </div>
          </div>

          <div className="test-report-chartpane">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 10, right: 6, left: 6, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.12} />
                <XAxis dataKey="name" tick={{fontSize:12}} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="correct" name="Correct" barSize={14} radius={[6,6,0,0]} />
                <Bar dataKey="wrong" name="Wrong" barSize={14} radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="test-report-table-section">
          <div className="test-report-table-toolbar">
            <div className="test-report-toolbar-left">Showing <strong>{filteredRows.length}</strong> rows</div>
          </div>

          <div className="test-report-table-wrap">
            <table className="test-report-table" role="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Quiz ID</th>
                  <th>Student ID</th>
                  <th>Q ID</th>
                  <th>Selected</th>
                  <th>Correct</th>
                  <th>Marks</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((r, idx)=> (
                    <tr key={r.id || idx} className={idx % 2 === 0 ? 'even' : 'odd'}>
                      <td>{r.id}</td>
                      <td>{r.quiz_id}</td>
                      <td>{r.student_id}</td>
                      <td>{r.question_id}</td>
                      <td>{r.selected_option}</td>
                      <td className={r.selected_option === r.correct_option ? 'cell-correct' : 'cell-wrong'}>{r.correct_option}</td>
                      <td>{r.obtained_marks}</td>
                      <td>{r.remarks ?? '—'}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="8" style={{textAlign:'center'}}>No data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <footer className="test-report-footer">
        <div>Designed with care • Light premium theme</div>
        <div className="test-report-footer-right">{new Date().toLocaleString()}</div>
      </footer>
    </div>
  );
}


/* -------------------------
   Helper functions
   ------------------------- */
function generateChartData(rows){
  const map = {};
  rows.forEach(r => {
    const key = r.quiz_id;
    if(!map[key]) map[key] = { name: key, correct: 0, wrong: 0 };
    if(Number(r.obtained_marks)) map[key].correct++;
    else map[key].wrong++;
  });
  return Object.values(map).slice(0, 10);
}
